import React, { useState, useEffect } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { Menu, Dropdown, Checkbox, Row, Col, Select, Switch, Tooltip, Divider } from 'antd';
import PropTypes from 'prop-types';

import {
  DropdownIcon,
  DragIcon,
  DescriptionIcon,
  DeleteIcon,
  DiscountIcon,
  DefaultImageIcon,
  CloseIcon,
  PlusCircleIcon,
  CheckboxIcon,
  InfoIcon,
} from 'components/Icons';
import { getEffectiveCheckboxOption, getEffectiveSelectable } from 'helpers/proposal';
import Input from 'components/Input';
import InputNumber from 'components/InputNumber';
import EditableSelect from 'components/EditableSelect';
import UploadContentLibraryItem from 'components/UploadContentLibraryItem';
import helpers from 'helpers';
import utils from 'utils/utils';

/**
 * Dropdown menu for the price component
 * @param {Object} x - List item (may have checkboxOption: { on, editableForClient, markedByDefault })
 * @param {Array} showenDescription - Array of description IDs
 * @param {Function} showHideDescription - Function to show/hide description
 * @param {Array} showenDiscount - Array of discount IDs
 * @param {Function} showHideDiscount - Function to show/hide discount
 * @param {Function} removeItem - Function to remove item
 * @param {boolean} price - Whether the item has a price
 * @param {Function} setShowSaveDeliverableModal - Function to set the show save deliverable modal
 * @param {Function} setSelectedDeliverable - Function to set the selected deliverable
 * @param {boolean|string} globalSelectable - Legacy: true | 'locked' | undefined/false
 * @param {Function} editCheckboxOption - Function to edit the checkbox option
 */
const dropdownMenu = (
  x,
  showenDescription,
  showHideDescription,
  showenDiscount,
  showHideDiscount,
  removeItem,
  price,
  setShowSaveDeliverableModal,
  setSelectedDeliverable,
  globalSelectable,
  editCheckboxOption
) => {
  const opt = getEffectiveCheckboxOption(x, globalSelectable);

  return (
    <Menu className="tablist-options-dropdown-menu" triggerSubMenuAction="click">
      {price && editCheckboxOption && (
        <Menu.SubMenu
          key="optional-checkbox"
          popupClassName="tablist-optional-checkbox-submenu"
          icon={<CheckboxIcon />}
          popupOffset={[-3, -7]}
          title={<span>Optional item (checkbox)</span>}>
          <Menu.Item key="opt-on" className="tablist-optional-checkbox-item">
            <Switch
              size="small"
              checked={opt.on}
              onChange={(checked, event) => {
                event.stopPropagation();
                editCheckboxOption(x, {
                  on: checked,
                  editableForClient: opt.editableForClient,
                  markedByDefault: opt.markedByDefault,
                });
              }}
            />
            <span className="tablist-optional-label">{opt.on ? 'On' : 'Off'}</span>
          </Menu.Item>

          <Divider />

          {opt.on && (
            <>
              <Menu.Item key="opt-editable" className="tablist-optional-checkbox-item">
                <Checkbox
                  checked={opt.editableForClient}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    editCheckboxOption(x, {
                      on: opt.on,
                      editableForClient: e.target.checked,
                      markedByDefault: opt.markedByDefault,
                    })
                  }
                />
                <span className="tablist-optional-label">Editable for client</span>
                <Tooltip
                  title={`${
                    opt.editableForClient
                      ? 'The prospect can choose the deliverables'
                      : "The prospect can't tick/untick the deliverables"
                  }`}>
                  <InfoIcon className="tablist-optional-checkbox-info-icon" />
                </Tooltip>
              </Menu.Item>
              <Menu.Item key="opt-marked" className="tablist-optional-checkbox-item">
                <Checkbox
                  checked={opt.markedByDefault}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    editCheckboxOption(x, {
                      on: opt.on,
                      editableForClient: opt.editableForClient,
                      markedByDefault: e.target.checked,
                    })
                  }
                />
                <span className="tablist-optional-label">Marked by default</span>
                <Tooltip title="Deliverables will be marked by default">
                  <InfoIcon className="tablist-optional-checkbox-info-icon" />
                </Tooltip>
              </Menu.Item>
            </>
          )}
        </Menu.SubMenu>
      )}

      {showenDescription.indexOf(x.id || x.name) === -1 && !x.description && (
        <Menu.Item
          key="1"
          icon={<DescriptionIcon />}
          onClick={() => showHideDescription(x, 'push')}>
          Add Description
        </Menu.Item>
      )}

      {(showenDescription.indexOf(x.id || x.name) !== -1 || x.description) && (
        <Menu.Item key="2" icon={<DescriptionIcon />} onClick={() => showHideDescription(x, 'pop')}>
          Remove Description
        </Menu.Item>
      )}

      {price && showenDiscount.indexOf(x.id || x.name) === -1 && !x.discount && (
        <Menu.Item key="3" icon={<DiscountIcon />} onClick={() => showHideDiscount(x, 'push')}>
          Add Discount
        </Menu.Item>
      )}

      {((price && showenDiscount.indexOf(x.id || x.name) !== -1) || x.discount) && (
        <Menu.Item key="4" icon={<DiscountIcon />} onClick={() => showHideDiscount(x, 'pop')}>
          Remove Discount
        </Menu.Item>
      )}

      <Menu.Item
        key="5"
        icon={<PlusCircleIcon className="tablist-input-options-save-icon" />}
        onClick={() => {
          setShowSaveDeliverableModal(true);
          setSelectedDeliverable({ ...x });
        }}>
        Save Deliverable
      </Menu.Item>

      <Menu.Item key="6" icon={<DeleteIcon />} onClick={removeItem(x)}>
        Delete
      </Menu.Item>
    </Menu>
  );
};

const Price = ({
  displayType,
  key,
  price,
  listIndex,
  valuePayemntType,
  x,
  t,
  propCurr,
  quantity,
  selectable,
  handleTotalChange,
  editName,
  configText,
  editQuantity,
  editPrice,
  editItemDiscount,
  editDescription,
  showenDescription,
  showHideDescription,
  showenDiscount,
  showHideDiscount,
  handleOptionsDropdown,
  removeItem,
  discountValue,
  language,
  customColumnsValues,
  editCustomColumnValue,
  thumbnail,
  editThumbnail,
  setShowSaveDeliverableModal,
  selectedDeliverable,
  setSelectedDeliverable,
  handleManageImages,
  showOrHidesUploadModal,
  setShowOrHidesUploadModal,
  editCheckboxOption,
  globalSelectable,
}) => {
  const effectiveSelectable = getEffectiveSelectable(x, globalSelectable ?? selectable);
  const effectiveCheckboxOption = getEffectiveCheckboxOption(x, globalSelectable ?? selectable);
  const checkboxChecked =
    typeof x.checked !== 'undefined' ? x.checked : effectiveCheckboxOption.markedByDefault;

  const [quantities, setQuantities] = useState({
    items: x.items,
    quantity: x.quantity,
    years: x.years,
  });

  // const presentPrice = x.currentPrice || x.price;
  // const [priceValue, setPriceValue] = useState(presentPrice); //helpers.commafi({ prop, x: x.price, curr: propCurr })
  // const [currentPriceValue] = useState(Number.isNaN(x.currentPrice) ? 0 : x.currentPrice);

  // let getStatePrice = (x) => {
  //   console.log('x', x)
  //   if (x.discount && (currentPriceValue !== undefined || currentPriceValue !== '')) {
  //     if (x.discount.value >= 100 && x.discount.unit === '%') {
  //       console.log('yello', currentPriceValue);
  //       const _currentPriceValue = parseFloat(currentPriceValue);
  //       if (Number.isNaN(_currentPriceValue) && x.currentPrice !== '') {
  //         setPriceValue(Number.isNaN(x.currentPrice) ? 0 : x.currentPrice);
  //       } else {
  //         console.log('4', _currentPriceValue);
  //         setPriceValue(Number.isNaN(_currentPriceValue) ? 0 : _currentPriceValue);
  //       }
  //     }
  //   }
  // };

  // useEffect(() => {
  //   getStatePrice(x);
  //   //eslint-disable-next-line
  // }, [x]);

  /**
   * Handle price value change
   * @param {Event} event - Event object
   * @param {boolean} isBlur - Whether the event is a blur event
   */
  const handlePriceValue = (event, isBlur) => {
    // setPriceValue(event.target.value);
    // passing number
    editPrice(x)(event.target.value, isBlur);
  };

  /**
   * Handle quantity change
   * @param {string} value - The value to set
   * @param {string} type - The type of quantity to change
   */
  const handleQuantityChange = (value, type = 'items') => {
    // type = items | quantity | years
    value = utils.onlyNumber(value);

    // converting '1.' or '1.0' to number will result into 1,
    // thus it will prevent entering 1.05
    if (typeof value === 'string' && !value.endsWith('.') && !value.endsWith('.0')) {
      value = Number(value) || '';
    }
    setQuantities((prev) => ({ ...prev, [type]: value }));
    editQuantity(value, x, type);
  };

  /**
   * Show discount
   * @returns {boolean} - Whether to show the discount
   */
  const showDiscount = price && (showenDiscount.indexOf(x.id || x.name) !== -1 || x.discount);

  /**
   * Recurring types
   * @returns {Array} - Array of recurring types
   */
  const recurringTypes = ['Hourly', 'Monthly', 'Annually'];

  /**
   * Is hourly
   * @returns {boolean} - Whether the item is hourly
   */
  const isHourly = (x.type === 'Hourly' && price) || (valuePayemntType === 'Hourly' && !price);
  const isMonthly = (x.type === 'Monthly' && price) || (valuePayemntType === 'Monthly' && !price);
  const isAnnually =
    (x.type === 'Annually' && price) || (valuePayemntType === 'Annually' && !price);

  // Update quantities when item changes (e.g., when quantity is set from prelist)
  useEffect(() => {
    setQuantities({
      items: x.items,
      quantity: x.quantity,
      years: isAnnually ? x.years || x.quantity : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x.items, x.quantity, x.years]);

  /**
   * Handle edit thumbnail
   * @param {Array} urls
   */
  const handleEditThumbnail = (urls) => {
    const { id } = selectedDeliverable;
    editThumbnail(id, [...(selectedDeliverable?.images || []), ...urls]);
    setSelectedDeliverable(null);
    setShowOrHidesUploadModal(false);
  };

  /**
   * Handle select deliverable
   */
  const handleSelectDeliverable = () => {
    setSelectedDeliverable({ ...x });
    setShowOrHidesUploadModal(true);
  };

  /**
   * Handle clear thumbnail
   * @param {Event} e
   */
  const handleClearThumbnail = (e) => {
    e.stopPropagation();
    editThumbnail(x.id, []);
  };

  /**
   * Render thumbnail
   */
  const renderThumbnail = () => {
    return (
      <div className="tablist-input-thumbnail">
        {x.images?.[0]?.url ? (
          <div
            className="tablist-input-thumbnail-container"
            onClick={() => handleManageImages(x, t)}>
            <CloseIcon
              className="tablist-input-thumbnail-close-icon"
              onClick={handleClearThumbnail}
            />
            <img src={x.images?.[0]?.url} alt={x.images?.[0]?.name || x.name || 'product'} />
            {x.images && x.images.length > 1 && (
              <div className="tablist-input-thumbnail-count">+{x.images.length - 1}</div>
            )}
          </div>
        ) : (
          <div className="tablist-input-thumbnail-placeholder">
            <DefaultImageIcon onClick={handleSelectDeliverable} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tablist-sortlist-container" key={key}>
      <div className="handle">
        <DragIcon />
        <DragIcon />
      </div>
      <div key={(x.id || x.name) + listIndex} className="tablist-sortlist-content">
        <Row className="noselect tablist-input-container">
          <Col flex="auto" className="item-name-wrap">
            {effectiveSelectable && (
              <Checkbox
                checked={checkboxChecked}
                onChange={(e) => handleTotalChange(e, x)}
                disabled={effectiveSelectable === 'locked'}
              />
            )}
            {thumbnail && language !== 'hebrew' && renderThumbnail()}
            <Input
              containerclass="tablist-input"
              placeholder="Deliverable"
              value={x.name}
              onChange={editName(x)}
            />
            {thumbnail && language === 'hebrew' && renderThumbnail()}
          </Col>

          <Col
            className={`price-inputs inputs-${displayType} ${quantity ? 'inputs-quantity' : ''} 
            ${recurringTypes.includes(valuePayemntType) ? 'inputs-recurring' : ''}`}>
            {customColumnsValues?.map((customColumnsValue, customColumnsValueIndex) => (
              <div className="tablist-input custom">
                <Input
                  placeholder="Custom value"
                  key={customColumnsValueIndex}
                  autoSize={{ maxRows: 1 }}
                  value={customColumnsValue}
                  onChange={(e) => editCustomColumnValue(customColumnsValueIndex, e.target.value)}
                />
              </div>
            ))}

            {quantity && (
              <>
                {
                  // Hourly, Monthly or Annually

                  isHourly ? (
                    <InputNumber
                      min={0}
                      value={quantities.quantity}
                      containerclass="tablist-input tablist-quantity quantity 2"
                      placeholder={configText('No of Hours') || 'No. of Hours'}
                      onChange={(e) => handleQuantityChange(e.target.value, 'quantity')}
                      onHandleChange={(value) => handleQuantityChange(value, 'quantity')}
                    />
                  ) : null
                }

                {isMonthly ? (
                  <InputNumber
                    min={0}
                    value={quantities.quantity}
                    containerclass="tablist-input tablist-quantity quantity"
                    placeholder={configText('No of Months') || 'No. of Months'}
                    onChange={(e) => handleQuantityChange(e.target.value, 'quantity')}
                    onHandleChange={(value) => handleQuantityChange(value, 'quantity')}
                  />
                ) : null}

                {isAnnually ? (
                  <InputNumber
                    min={0}
                    containerclass="tablist-input tablist-quantity quantity"
                    value={quantities.years}
                    placeholder={configText('No of Years') || 'No. of Years'}
                    onChange={(e) => handleQuantityChange(e.target.value, 'years')}
                    onHandleChange={(value) => handleQuantityChange(value, 'years')}
                  />
                ) : null}

                {
                  // empty container to cover the empty space for duration
                  !isHourly && !isMonthly && !isAnnually && (
                    <div className="tablist-input tablist-quantity quantity empty-placeholder" />
                  )
                }

                {/* no. of items input */}
                <InputNumber
                  min={0}
                  containerclass="tablist-input tablist-items items"
                  value={quantities.items}
                  placeholder={configText('No of Items') || 'No. of Items'}
                  onChange={(e) => handleQuantityChange(e.target.value, 'items')}
                  onHandleChange={(value) => handleQuantityChange(value, 'items')}
                />
              </>
            )}

            {displayType === 'table' && (
              <div className="price">
                <div className="currency">{helpers.shorten(propCurr, 3, '...')}</div>
                <div>
                  {price ? (
                    <Input
                      containerclass="tablist-input tablist-price"
                      value={
                        typeof x.currentPrice === 'undefined' ? x?.price || 0 : x?.currentPrice
                      }
                      onChange={handlePriceValue}
                      onBlur={(e) => handlePriceValue(e, true)}
                      placeholder="Price"
                    />
                  ) : null}

                  {showDiscount && (
                    <div className="discount-container">
                      <>
                        <Input
                          containerclass="tablist-discount"
                          value={discountValue}
                          onChange={(e) => editItemDiscount(e, x, 'value')}
                          placeholder={configText('discount text') || 'Discount'}
                        />
                        <Select
                          value={!x?.discount?.unit || x?.discount?.unit === '%' ? '%' : propCurr}
                          onChange={(value) => {
                            if (value === '%' || value === propCurr) {
                              editItemDiscount(null, x, 'unit', value);
                            }
                          }}
                          className="tax-unit-select price-units"
                          dropdownClassName="is-legacy"
                          suffixIcon={<DropdownIcon />}>
                          <Select.Option value={propCurr}>{propCurr}</Select.Option>
                          <Select.Option value="%">%</Select.Option>
                        </Select>
                      </>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="price-options">
              {price ? (
                <EditableSelect
                  items={['One-Time', 'Monthly', 'Annually', 'Hourly']}
                  selectClassName="price-options-dropdown"
                  inputClassName="price-options-input"
                  placeholder="Payment Type"
                  x={x}
                  handleOptionsDropdown={handleOptionsDropdown}
                  configText={configText}
                  dropdownClassName={
                    language === 'hebrew' || language === 'arabic' ? 'wizard-language-rtl' : ''
                  }
                  suffixIcon={<DropdownIcon />}
                />
              ) : null}

              <Dropdown
                overlay={() =>
                  dropdownMenu(
                    x,
                    showenDescription,
                    showHideDescription,
                    showenDiscount,
                    showHideDiscount,
                    removeItem,
                    price,
                    setShowSaveDeliverableModal,
                    setSelectedDeliverable,
                    globalSelectable ?? selectable,
                    editCheckboxOption
                  )
                }
                trigger={['click']}
                placement="bottomRight">
                <MoreOutlined
                  className="tablist-input-options"
                  onClick={(e) => e.preventDefault()}
                />
              </Dropdown>

              {showDiscount && (
                <DeleteIcon
                  className="input-discount-delete"
                  onClick={() => showHideDiscount(x, 'pop')}
                />
              )}
            </div>
          </Col>
        </Row>

        {(showenDescription.indexOf(x.id || x.name) !== -1 || x.description) && (
          <Input
            containerclass="tablist-input-description"
            type="textarea"
            autoSize={{ minRows: 1 }}
            placeholder="Enter Description"
            value={x.description}
            onChange={editDescription(x)}
            allowClear
          />
        )}
      </div>

      <UploadContentLibraryItem
        type="pricing"
        showUploadModal={showOrHidesUploadModal}
        cancelUploadModal={() => setShowOrHidesUploadModal(false)}
        onSuccess={handleEditThumbnail}
      />
    </div>
  );
};

/**
 * Default props for the price component
 * @returns {Object} - Default props
 */
Price.defaultProps = {
  displayType: 'table',
  selectable: '',
  quantity: false,
  valuePayemntType: false,
  removeItem: () => {},
  showHideDiscount: () => {},
  editItemDiscount: () => {},
  showenDiscount: [],
  language: '',
  customColumnsValues: [],
  showOrHidesUploadModal: false,
  setShowOrHidesUploadModal: () => {},
  selectedDeliverable: null,
  setSelectedDeliverable: () => {},
  editCheckboxOption: null,
  globalSelectable: undefined,
};

/**
 * Prop types for the price component
 * @returns {Object} - Prop types
 */
Price.propTypes = {
  displayType: PropTypes.string,
  currentPrice: PropTypes.string,
  price: PropTypes.bool.isRequired,
  x: PropTypes.instanceOf(Object).isRequired,
  propCurr: PropTypes.string.isRequired,
  listIndex: PropTypes.string.isRequired,
  handleTotalChange: PropTypes.func.isRequired,
  editName: PropTypes.func.isRequired,
  configText: PropTypes.instanceOf(Object).isRequired,
  editQuantity: PropTypes.func.isRequired,
  editPrice: PropTypes.func.isRequired,
  editDescription: PropTypes.func.isRequired,
  editItemDiscount: PropTypes.func,
  showenDescription: PropTypes.instanceOf(Array).isRequired,
  showHideDescription: PropTypes.func.isRequired,
  showenDiscount: PropTypes.instanceOf(Array),
  showHideDiscount: PropTypes.func,
  handleOptionsDropdown: PropTypes.func.isRequired,
  selectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  quantity: PropTypes.bool,
  removeItem: PropTypes.func,
  valuePayemntType: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  language: PropTypes.string,
  customColumnsValues: PropTypes.instanceOf(Array),
  showOrHidesUploadModal: PropTypes.bool,
  setShowOrHidesUploadModal: PropTypes.func,
  selectedDeliverable: PropTypes.instanceOf(Object),
  setSelectedDeliverable: PropTypes.func,
  editCheckboxOption: PropTypes.func,
  globalSelectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default Price;
